/**
 * 地図手書きメモのモデレーション hook (docs/24 P5b、admin)。
 *
 * `districts/{districtId}/mapAnnotations/{uid}` を購読し、管理者が各メモを
 * 非表示 (archivedAt + visibility='hidden_all') / 復元 / 完全削除できるようにする。
 * firestore.rules は admin (isDistrictAdmin) の update/delete を許可済。
 */
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  updateDoc,
  type DocumentData,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../lib/firebase';

export interface MapMemoSummary {
  authorUid: string;
  authorNickname: string | null;
  sessionId: string | null;
  strokeCount: number;
  visibility: 'shared' | 'hidden_all';
  archivedAt: string | null;
  updatedAt: string | null;
}

export type MapMemoModerationState =
  | { status: 'loading' }
  | { status: 'ready'; memos: MapMemoSummary[] }
  | { status: 'error'; error: Error };

function parse(id: string, data: DocumentData): MapMemoSummary {
  return {
    authorUid: typeof data.authorUid === 'string' ? data.authorUid : id,
    authorNickname: typeof data.authorNickname === 'string' ? data.authorNickname : null,
    sessionId: typeof data.sessionId === 'string' ? data.sessionId : null,
    strokeCount: Array.isArray(data.strokes) ? data.strokes.length : 0,
    visibility: data.visibility === 'hidden_all' ? 'hidden_all' : 'shared',
    archivedAt: typeof data.archivedAt === 'string' ? data.archivedAt : null,
    updatedAt: typeof data.updatedAt === 'string' ? data.updatedAt : null,
  };
}

export interface UseMapMemoModerationResult {
  state: MapMemoModerationState;
  /** 非表示化 (ユーザーアプリの共有表示から除外)。 */
  archive: (authorUid: string) => Promise<void>;
  /** 非表示を解除して再共有。 */
  restore: (authorUid: string) => Promise<void>;
  /** doc ごと完全削除。 */
  remove: (authorUid: string) => Promise<void>;
}

export function useMapMemoModeration(
  districtId: string | null,
  uid: string,
): UseMapMemoModerationResult {
  const [state, setState] = useState<MapMemoModerationState>({ status: 'loading' });

  useEffect(() => {
    if (!districtId) return;
    const col = collection(db, 'districts', districtId, 'mapAnnotations');
    const unsub = onSnapshot(
      col,
      (snap) => {
        const memos = snap.docs.map((d) => parse(d.id, d.data()));
        memos.sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));
        setState({ status: 'ready', memos });
      },
      (e) => setState({ status: 'error', error: e as Error }),
    );
    return () => unsub();
  }, [districtId]);

  async function archive(authorUid: string) {
    if (!districtId) return;
    await updateDoc(doc(db, 'districts', districtId, 'mapAnnotations', authorUid), {
      visibility: 'hidden_all',
      archivedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      updatedBy: uid,
    });
  }

  async function restore(authorUid: string) {
    if (!districtId) return;
    await updateDoc(doc(db, 'districts', districtId, 'mapAnnotations', authorUid), {
      visibility: 'shared',
      archivedAt: null,
      updatedAt: new Date().toISOString(),
      updatedBy: uid,
    });
  }

  async function remove(authorUid: string) {
    if (!districtId) return;
    await deleteDoc(doc(db, 'districts', districtId, 'mapAnnotations', authorUid));
  }

  return { state, archive, restore, remove };
}
